import React, { useEffect, useState, useRef } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import BackButton from '../components/BackButton';
import { Users, GraduationCap, HandshakeIcon, Shield, Building2, ShoppingCart, Truck, Leaf, Award, CheckCircle, Target, Clock, Wallet, HeadphonesIcon, ArrowRight, Sparkles } from 'lucide-react';

import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

// Animation hook
const useInView = (threshold = 0.1) => {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setIsInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsInView(true);
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isInView];
};

export default function ServicesPage() {
  const [heroRef, heroInView] = useInView();
  const [servicesRef, servicesInView] = useInView();
  const [advantagesRef, advantagesInView] = useInView();

  const services = [
    {
      icon: ShoppingCart,
      title: 'Communityplace',
      subtitle: 'Achats mutualisés et échanges professionnels',
      description: 'La place de marché de l’écosystème O’SCOP pour rapprocher acheteurs et fournisseurs et organiser les approvisionnements.',
      features: [
        'Accès à la centrale professionnelle',
        'Présentation des besoins d’achat',
        'Référencement des fournisseurs',
        'Orientation vers les solutions logistiques'
      ],
      gradient: 'from-indigo-500 to-blue-500',
      bgGlow: 'bg-indigo-500/20',
      link: 'https://centrale.objectifscopoutremer.com',
      external: true
    },
    {
      icon: Users,
      title: "O'SCOP GE",
      subtitle: "Groupement d'Employeurs",
      description: "Mutualisation des emplois et sécurisation RH pour les TPE/PME ultramarines.",
      features: [
        "Mise à disposition de personnel qualifié",
        "Gestion administrative et paie",
        "Accompagnement RH personnalisé",
        "Flexibilité et optimisation des coûts"
      ],
      gradient: "from-blue-500 to-cyan-500",
      bgGlow: "bg-blue-500/20",
      link: "/oscop-ge"
    },
    {
      icon: GraduationCap,
      title: "FORMA'SCOP",
      subtitle: "Formation Professionnelle",
      description: "Centre de formation coopératif pour le développement des compétences.",
      features: [
        "Parcours de formation selon le besoin",
        "Accompagnement à la reconversion",
        "Modules sur-mesure pour entreprises",
        "Modalités à préciser avec l’équipe"
      ],
      gradient: "from-purple-500 to-pink-500",
      bgGlow: "bg-purple-500/20",
      link: "/formascop"
    },
    {
      icon: Shield,
      title: "COPPAM",
      subtitle: "Garanties Solidaires",
      description: "Mécanisme coopératif d'entraide et de prévention destiné aux membres ESS.",
      features: [
        "Accompagnement solidaire fondé sur le Code civil",
        "Expertise, prévention et gestion des risques",
        "Soutien en cas de difficultés techniques",
        "Appui lors d'un dommage"
      ],
      gradient: "from-green-500 to-emerald-500",
      bgGlow: "bg-green-500/20",
      link: "/garanties",
      note: "(Sans constituer une assurance)"
    },
    {
      icon: Building2,
      title: "FOGEDOM-SCIC",
      subtitle: "Expertise & Solidarité",
      description: "Fonds d'expertise et de solidarité coopérative pour les Outre-mer.",
      features: [
        "Appui technique aux projets",
        "Expertise civile et analyse",
        "Soutien solidaire hors assurantiel",
        "Accompagnement au développement"
      ],
      gradient: "from-red-500 to-orange-500",
      bgGlow: "bg-red-500/20",
      link: "/fogedom/presentation"
    },
    {
      icon: Truck,
      title: "LOGI'SCOP",
      subtitle: "Logistique",
      description: "Solutions logistiques adaptées aux territoires ultramarins.",
      features: [
        "Transport et livraison",
        "Stockage et entreposage",
        "Gestion des flux",
        "Solutions sur-mesure"
      ],
      gradient: "from-orange-500 to-amber-500",
      bgGlow: "bg-orange-500/20",
      link: "/logistique-mobilite"
    },
    {
      icon: Leaf,
      title: "AGRO'SCOP",
      subtitle: "Agro-transformation",
      description: "Accompagnement des filières agricoles et agro-alimentaires locales.",
      features: [
        "Transformation des produits locaux",
        "Valorisation des circuits courts",
        "Accompagnement technique",
        "Développement durable"
      ],
      gradient: "from-emerald-500 to-green-500",
      bgGlow: "bg-emerald-500/20",
      link: "/agro-vegetal"
    }
  ];

  const advantages = [
    { icon: Target, title: "Accompagnement personnalisé", desc: "Un interlocuteur dédié pour chaque entreprise" },
    { icon: Wallet, title: "Tarifs solidaires", desc: "Des prix adaptés aux réalités des Outre-mer" },
    { icon: Clock, title: "Réactivité", desc: "Des réponses rapides à vos besoins" },
    { icon: Award, title: "Expertise locale", desc: "Une connaissance approfondie des territoires" },
    { icon: HandshakeIcon, title: "Réseau coopératif", desc: "Accès à un écosystème de partenaires" },
    { icon: HeadphonesIcon, title: "Contact et orientation", desc: "Un point de contact pour préciser votre demande" }
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Navigation />
      
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="relative min-h-[70vh] flex items-center overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950"
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1920&q=80" 
            alt="Échange autour d’un projet professionnel"
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-900/90 to-slate-950" />
        </div>
        
        {/* Animated background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/3 -left-32 w-[600px] h-[600px] bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/3 -right-32 w-[500px] h-[500px] bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20">
          <BackButton to="/" label="Retour à l'accueil" className="text-white/70 mb-8 hover:text-white transition-colors" />
          
          <div className={`transition-transform duration-1000 ${heroInView ? 'translate-y-0' : 'translate-y-2'}`}>
            <div className="flex items-center gap-3 mb-6">
              <Badge className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-300 border border-blue-500/30 px-4 py-2">
                <Sparkles className="w-4 h-4 mr-2" />
                Solutions coopératives pour les Outre-mer
              </Badge>
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6">
              <span className="bg-gradient-to-r from-white via-blue-100 to-white bg-clip-text text-transparent">
                Nos Services
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mb-10 leading-relaxed">
              Achats, emploi partagé, formation, logistique et accompagnement solidaire :
              découvrez les services de l’écosystème O’SCOP dans les <span className="text-amber-400 font-semibold">territoires ultramarins</span>.
            </p>

            <div className="flex flex-wrap gap-4">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-bold px-8 py-6 text-lg shadow-2xl shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-300 hover:scale-105"
                onClick={() => window.location.href = '/contact'}
              >
                Demander un devis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-white/20 text-white hover:bg-white/10 px-8 py-6 text-lg transition-all duration-300"
                onClick={() => window.location.href = '/register'}
              >
                Créer mon espace
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section 
        ref={servicesRef}
        className="py-24 bg-gradient-to-b from-slate-950 to-slate-900 relative"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <a
                key={index}
                href={service.link}
                target={service.external ? '_blank' : undefined}
                rel={service.external ? 'noopener noreferrer' : undefined}
                className={`group relative block bg-slate-800/30 backdrop-blur-xl rounded-3xl p-8 border border-slate-700/50 hover:border-slate-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-300 transition-all duration-500 hover:-translate-y-3 hover:shadow-2xl ${servicesInView ? 'translate-y-0' : 'translate-y-2'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {/* Glow effect */}
                <div className={`absolute -inset-0.5 ${service.bgGlow} rounded-3xl blur-xl opacity-0 group-hover:opacity-50 transition-opacity duration-500`} />
                
                <div className="relative">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                    <service.icon className="w-8 h-8 text-white" />
                  </div>
                  
                  <div className="mb-4">
                    <h2 className="text-2xl font-bold text-white group-hover:text-amber-400 transition-colors">{service.title}</h2>
                    <span className="text-sm text-slate-400">{service.subtitle}</span>
                  </div>
                  
                  <p className="text-slate-400 mb-6 leading-relaxed">{service.description}</p>
                  
                  <ul className="space-y-3 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-3 text-sm text-slate-300">
                        <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${service.gradient} flex items-center justify-center flex-shrink-0`}>
                          <CheckCircle className="w-3 h-3 text-white" />
                        </div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  {service.note && (
                    <p className="text-xs text-slate-500 italic mb-4">{service.note}</p>
                  )}
                  
                  <div className="flex items-center text-amber-400 font-semibold group-hover:gap-3 gap-2 transition-all">
                    En savoir plus
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section 
        ref={advantagesRef}
        className="py-24 bg-slate-900 relative overflow-hidden"
      >
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-gradient-to-b from-amber-600/10 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`text-center mb-16 transition-transform duration-1000 ${advantagesInView ? 'translate-y-0' : 'translate-y-2'}`}>
            <Badge className="bg-amber-500/20 text-amber-300 border border-amber-500/30 mb-4 px-4 py-2">
              <Award className="w-4 h-4 mr-2" />
              Pourquoi O'SCOP ?
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Des avantages <span className="text-amber-400">concrets</span> pour votre activité
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {advantages.map((adv, index) => (
              <div 
                key={index} 
                className={`group bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50 hover:border-amber-500/50 transition-all duration-300 hover:-translate-y-2 text-center ${advantagesInView ? 'translate-y-0' : 'translate-y-2'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center group-hover:scale-110 transition-transform border border-amber-500/30">
                  <adv.icon className="w-7 h-7 text-amber-400" />
                </div>
                <h3 className="font-bold text-white mb-2 text-sm">{adv.title}</h3>
                <p className="text-xs text-slate-400">{adv.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-amber-600 via-orange-600 to-amber-700 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.1)_1px,transparent_1px)] bg-[size:40px_40px]" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Besoin d'un <span className="text-amber-200">accompagnement</span> sur mesure ?
          </h2>
          <p className="text-xl text-white/80 mb-10">
            Nos équipes sont à votre disposition pour étudier vos besoins et vous proposer les solutions adaptées.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-amber-600 hover:bg-slate-100 font-bold px-10 py-6 text-lg shadow-2xl transition-all duration-300 hover:scale-105"
              onClick={() => window.location.href = '/contact'}
            >
              Nous contacter
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 px-10 py-6 text-lg transition-all duration-300"
              onClick={() => window.location.href = '/register'}
            >
              Créer mon espace
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
